"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { GaugeChart } from "@/src/components/charts/gauge-chart";

export default function DocsGaugeChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Gauge Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Display a single value against thresholds with visual indicators.
              Perfect for KPIs, completion status, and performance metrics.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Project Completion</CardTitle>}
            contentClassName="p-theme-5"
          >
            <GaugeChart
              value={75}
              min={0}
              max={100}
              label="Progress"
              suffix="%"
              height={350}
            />
          </SectionCard>
        </Section>

        {/* Variants */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>With Title</CardTitle>}
              contentClassName="p-theme-5"
            >
              <GaugeChart
                value={85}
                min={0}
                max={100}
                title="Accuracy Score"
                subtitle="Model Performance"
                label="Score"
                suffix="%"
                height={300}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Custom Range</CardTitle>}
              contentClassName="p-theme-5"
            >
              <GaugeChart
                value={65}
                min={0}
                max={200}
                label="Units Processed"
                suffix="/200"
                height={300}
              />
            </SectionCard>
          </div>
        </Section>

        {/* Different Values */}
        <Section title="Different Completion Levels">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-theme-4">
            <SectionCard
              header={<CardTitle>Low</CardTitle>}
              contentClassName="p-theme-5"
            >
              <GaugeChart
                value={35}
                min={0}
                max={100}
                label="Status"
                suffix="%"
                height={250}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Medium</CardTitle>}
              contentClassName="p-theme-5"
            >
              <GaugeChart
                value={55}
                min={0}
                max={100}
                label="Status"
                suffix="%"
                height={250}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>High</CardTitle>}
              contentClassName="p-theme-5"
            >
              <GaugeChart
                value={92}
                min={0}
                max={100}
                label="Status"
                suffix="%"
                height={250}
              />
            </SectionCard>
          </div>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>GaugeChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">value</td>
                  <td className="py-2 px-2">number</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Current value to display</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">min</td>
                  <td className="py-2 px-2">number</td>
                  <td className="py-2 px-2">0</td>
                  <td className="py-2 px-2">Minimum value in range</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">max</td>
                  <td className="py-2 px-2">number</td>
                  <td className="py-2 px-2">100</td>
                  <td className="py-2 px-2">Maximum value in range</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">label</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Label text below gauge</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">suffix</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">""</td>
                  <td className="py-2 px-2">Text appended to value (%, /200)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">280</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">thresholds</td>
                  <td className="py-2 px-2">array</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">{"Custom threshold colors [{value, color}]"}</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Usage Examples */}
        <Section title="Usage Examples">
          <SectionCard
            header={<CardTitle>Code Examples</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`// Basic gauge
<GaugeChart
  value={75}
  min={0}
  max={100}
  label="Progress"
  suffix="%"
/>

// With custom thresholds
<GaugeChart
  value={65}
  min={0}
  max={200}
  label="Temperature"
  suffix="°C"
  thresholds={[
    { value: 60, color: "#3b82f6" },  // Cold
    { value: 120, color: "#f59e0b" }, // Warm
    { value: 200, color: "#ef4444" }  // Hot
  ]}
/>

// With title and context
<GaugeChart
  value={85}
  min={0}
  max={100}
  title="Performance"
  subtitle="Last 30 days"
  label="Score"
  suffix="/100"
  height={350}
/>`}</code>
            </pre>
          </SectionCard>
        </Section>

        {/* Threshold System */}
        <Section title="Default Thresholds">
          <SectionCard
            header={<CardTitle>Automatic Color Mapping</CardTitle>}
            contentClassName="p-theme-5"
          >
            <div className="space-y-theme-3 text-sm">
              <Text>
                By default, gauges use these semantic color thresholds:
              </Text>

              <div className="space-y-theme-2">
                <div className="flex items-center gap-theme-3">
                  <div className="w-8 h-8 rounded bg-destructive flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-xs">0% - 60%</p>
                    <p className="text-muted-foreground text-xs">Error State (Red)</p>
                  </div>
                </div>

                <div className="flex items-center gap-theme-3">
                  <div className="w-8 h-8 rounded bg-warning flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-xs">60% - 80%</p>
                    <p className="text-muted-foreground text-xs">Warning State (Yellow)</p>
                  </div>
                </div>

                <div className="flex items-center gap-theme-3">
                  <div className="w-8 h-8 rounded bg-success flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-xs">80% - 100%</p>
                    <p className="text-muted-foreground text-xs">Success State (Green)</p>
                  </div>
                </div>
              </div>

              <Text className="text-xs text-muted-foreground pt-2">
                Pass the <code className="bg-muted px-1.5 py-0.5 rounded text-xs">thresholds</code> prop
                to customize colors and values.
              </Text>
            </div>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsGaugeChartPage as GaugeChartPage };
